import { createSelector } from 'reselect';
import { cwSelectors } from './courseware.selectors';
import { ActionMenuItemConfig } from '../../pages/course-plan/syllabus.models';
import {
  getActiveOrderedAddActionConfigs,
  getActiveSyllabusItemTypeFromCatalog
} from '../../pages/catalog/catalog.utilities';
import {
  CoursewareStore,
  FilterStateType,
  ReduxPage,
  ReduxPageWithAssessmentSubmissions,
  ReduxPageWithCatalog,
  ReduxPageWithFilter,
  ReduxPageWithPrimaryTaxonomies
} from './courseware.models';
import { hasVantage } from '../../utilities/app.utilities';

const {
  getFeatureFlagsGrouped,
  getEvolveProducts,
  getCourseSectionId,
  getCatalog,
  getCwStore,
  getRoleId,
  getCourse,
} = cwSelectors;

export const makeGetActiveOrderedAddActionConfigs = (orderedItemOptions: ActionMenuItemConfig[]) => {
  return createSelector(
    [getFeatureFlagsGrouped, getEvolveProducts, getCourseSectionId, getRoleId, getCatalog, getCourse],
    (featureFlagsGrouped, evolveProducts, courseSectionId, userRole, catalog, course) => {
      const activeSyllabusItemType = getActiveSyllabusItemTypeFromCatalog(catalog, userRole);
      return getActiveOrderedAddActionConfigs({
        featureFlagsGrouped,
        orderedItemOptions,
        evolveProducts,
        courseSectionId,
        catalog,
        userRole,
        activeSyllabusItemType,
        isVantageCourse: hasVantage(course),
      }).map((actionConfig) => {
        return actionConfig.action;
      });
    }
  );
};

const makeGetReduxPage = <T extends ReduxPage>(reduxPage: T) => (globalState): CoursewareStore[T] => getCwStore(globalState)[reduxPage];

export const makeGetCatalogInReduxPage = (reduxPage: ReduxPageWithCatalog) => {
  return createSelector(
    [makeGetReduxPage(reduxPage)],
    (page) => {
      return page.catalog;
    }
  );
};

export const makeGetPrimaryTaxonomiesInReduxPage = (reduxPage: ReduxPageWithPrimaryTaxonomies) => {
  return createSelector(
    [makeGetReduxPage(reduxPage)],
    (page) => {
      return page.primaryTaxonomies;
    }
  );
};

export const makeGetFilterStateInReduxPage = <T>(reduxPage: ReduxPageWithFilter, field: FilterStateType) => {
  return createSelector(
    makeGetReduxPage(reduxPage),
    (page) => {
      return page.filterState[field] as unknown as T;
    }
  );
};

export const makeGetAssessmentSubmissionsInReduxPage = (reduxPage: ReduxPageWithAssessmentSubmissions) => {
  return createSelector(
    [makeGetReduxPage(reduxPage)],
    (page) => {
      return page.assessmentSubmissionsMap;
    }
  );
};
